import type {
  PhotoMissingReason,
  PhotoPolicy,
  PunchPhotoStatus,
  TimeSession,
} from '@worklogix/shared';

/**
 * Pure, DOM-free helpers backing the Phase 2 photo-on-punch supervisor surface
 * (plan-v7 "Supervisor UI deltas"). Kept separate from the React components so
 * the URL construction, the missing-reason copy, the show-photo predicate and
 * the badge-state selection are trivially unit-testable without a DOM/fetch.
 *
 * SECURITY: URLs here point ONLY at the RLS-checked API proxy
 * (`GET /time/photos/:photoId`). There is deliberately NO SAS / direct-blob /
 * capability URL — every read re-passes the Manager/Admin guard + RLS
 * server-side (plan-v7 Decision 1). No PII is ever placed in a URL or logged.
 */

/**
 * Build the API path for the RLS-checked photo proxy for one punch. The metadata
 * DTO ({@link import('@worklogix/shared').PunchPhotoMeta}) exposes only
 * `punchId` (one photo per punch — `UNIQUE (tenant_id, kiosk_punch_id)`), so the
 * `:photoId` path param is addressed by the PUNCH id.
 *
 * INTEGRATION DEPENDENCY (Dev-B): this assumes `GET /time/photos/:photoId`
 * resolves the photo by its punch id. If Dev-B keys the route by a distinct
 * `punch_photo.id`, that id must be added to `PunchPhotoMeta` (Dev-A) — flag at
 * integration.
 */
export function photoProxyPath(punchId: string): string {
  return `/time/photos/${encodeURIComponent(punchId)}`;
}

/**
 * Join the API base origin with {@link photoProxyPath}. Tolerates a trailing
 * slash on `baseUrl` (mirrors the shared api client's join semantics).
 */
export function photoProxyUrl(baseUrl: string, punchId: string): string {
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${base}${photoProxyPath(punchId)}`;
}

/**
 * Whether a session should show a photo affordance at all.
 *
 * KEYED ON THE PUNCHES, NOT ON `source`. A photo exists if and only if there is a
 * punch to hang it on, and the punch links are exactly that fact.
 *
 * 🔥 THIS USED TO READ `source === 'kiosk'`, AND A SUPERVISOR CLOCK-OUT ERASED THE
 * CLOCK-IN PHOTO FROM THE SCREEN (reported 2026-09-14). `updateSession` stamps
 * `source = 'manual'` on any supervisor edit — including closing an open kiosk
 * shift — so the moment someone was clocked out from the website their punch photo
 * stopped rendering. The photo was never gone: the blob, the punch and
 * `open_punch_id` were all still there, and the row simply stopped asking for them.
 * A genuinely manual session has no punch ids, so it still shows nothing.
 */
export function sessionShowsPhoto(
  session: Pick<TimeSession, 'openPunchId' | 'closePunchId'>
): boolean {
  return session.openPunchId !== undefined || session.closePunchId !== undefined;
}

/**
 * Map the non-PII `missing_reason` enum to friendly supervisor copy for a
 * tooltip. Never surfaces capture detail beyond the closed enum. `undefined`
 * (reason absent) falls back to a generic, non-alarming line.
 */
export function missingReasonText(reason: PhotoMissingReason | undefined): string {
  switch (reason) {
    case 'no_camera':
      return 'No camera was available on the kiosk when this punch was recorded.';
    case 'permission_denied':
      return 'Camera access was denied on the kiosk when this punch was recorded.';
    case 'capture_failed':
      return 'The kiosk camera failed to capture an image for this punch.';
    case undefined:
      return 'No verification photo was captured for this punch.';
    default:
      return 'No verification photo was captured for this punch.';
  }
}

/**
 * The semantic badge state for a punch photo, selected from its stored/missing
 * status and (when known) the tenant capture policy. Pure so all four branches
 * are unit-testable:
 *   - `stored`                       → `none` (no badge; the thumbnail speaks)
 *   - `missing` + policy `optional`  → `missing` (informational placeholder)
 *   - `missing` + policy `required`  → `missing-required` (actionable warning)
 *   - `missing` + policy UNKNOWN     → `missing-required` (fail-safe: web-app is
 *     not currently told the tenant policy, so a missing photo escalates to the
 *     warning treatment rather than being silently downplayed — plan-v7).
 */
export type PhotoBadgeKind = 'none' | 'missing' | 'missing-required';

export function photoBadgeKind(
  status: PunchPhotoStatus,
  policy: PhotoPolicy | undefined
): PhotoBadgeKind {
  if (status === 'stored') {
    return 'none';
  }
  return policy === 'optional' ? 'missing' : 'missing-required';
}
